import { Typography, Button, MenuItem } from '@material-ui/core'
import React, { ChangeEvent, Dispatch, ReactNode } from 'react'
import { OrganizationsClient, AdminUserRequestDTO, UsersClient, RoleValues, UserWithOrganizationResponseDTO, UserResponseDTO } from '../generated/ApiClient'
import { Formik, Form, Field } from 'formik'
import { Select, TextField } from 'formik-material-ui'
import * as Yup from 'yup'
import { useDispatch } from "react-redux";
import { actionCreators as snackbar } from '../store/AlertSnackbarStore'
import ValuePickerControl from '../common/ValuePickerControl'
import { RouteComponentProps } from 'react-router'
import { OrganizationSettableRoles, UserFriendlyRoleNames, useIsUserInRoles, GlobalEditor, isInRoles, AllGlobal, GlobalAdmin } from '../hooks/useIsUserInRoles';

const OrgDetailsFormSchema = Yup.object().shape({
    email: Yup.string().max(100).required(),
})
// nothing to validate here
const UsernameFormSchema = Yup.object().shape({
})

type OrgDetailsFormProperties = Yup.InferType<typeof OrgDetailsFormSchema>
type UsernameFormProperties = Yup.InferType<typeof UsernameFormSchema>

async function formSubmitCallback(organizationId: string | undefined, userOrEmail: UserResponseDTO | string, role: RoleValues, dispatch: Dispatch<any>) {
    try {
        if (organizationId && userOrEmail) {
            const result = await new OrganizationsClient().addUser(organizationId, (typeof userOrEmail === 'string') ? userOrEmail : userOrEmail.azureId)
            // don't try to change roles if the user is in a global role
            if (typeof userOrEmail === 'string' || !isInRoles(AllGlobal, userOrEmail.role.Case)) {
                const requestData: AdminUserRequestDTO = { role: { Case: role } }
                await new UsersClient().update5(result.azureId, requestData)
            }
            dispatch(snackbar.addMessage({ title: "Success", message: `Member successfully added`, alertType: 'success' }))
        }
    }
    catch (error) {
        dispatch(snackbar.addError(error, undefined, 'Unable to save member'));
    }
}

export default function AddMemberForm(props: RouteComponentProps<{ organizationId: string }>) {
    const dispatch = useDispatch();
    const [selectedUserRole, setSelectedUserRole] = React.useState<RoleValues>('Operator');
    const [selectedUserId, setSelectedUserId] = React.useState('');
    const [orgUsers, setOrgUsers] = React.useState<UserWithOrganizationResponseDTO[]>([]);
    const userIsGlobalAdmin = useIsUserInRoles(GlobalAdmin)

    React.useEffect(() => {
        const fetchData = async () => {
            if (userIsGlobalAdmin) {
                const usersData = await new UsersClient().getMany3();

                if (usersData.length > 0) {
                    setOrgUsers(usersData);
                    setSelectedUserId(usersData[0].azureId)
                }
            }
        }
        fetchData();
    }, [props.match.params.organizationId, setOrgUsers, setSelectedUserId]);

    const userRoles = OrganizationSettableRoles.map(k => ({ text: UserFriendlyRoleNames[k], value: k }))

    const handleRoleChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: ReactNode) => {
        setSelectedUserRole(event.target.value as RoleValues)
    };

    const handleUserSelectedChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown; }>, child: ReactNode) => {
        let target = event.target as HTMLInputElement;
        setSelectedUserId(target.value)
    };

    return (
        <Formik initialValues={{ email: "" }}
            validationSchema={userIsGlobalAdmin ? UsernameFormSchema : OrgDetailsFormSchema}
            onSubmit={async (values, _) => {
                await formSubmitCallback(props.match.params.organizationId, userIsGlobalAdmin ? orgUsers.find(x => x.azureId === selectedUserId)! : (values as OrgDetailsFormProperties).email, selectedUserRole, dispatch)
            }}>
            {/* supply a function to formik so that we can access these props*/}
            {({ submitForm, isSubmitting, touched }) => (
                <>
                    <Form translate='yes'>
                        <div>
                            {userIsGlobalAdmin && orgUsers.length > 0 ?
                                <>
                                    <Typography paragraph display='inline'><b>Members:</b>&nbsp;</Typography>
                                    <Field
                                        component={Select}
                                        name={'email'}
                                        onChange={handleUserSelectedChange}
                                        value={selectedUserId}
                                    >
                                        {orgUsers.map(user => <MenuItem value={user.azureId} key={user.azureId}>{user.userName}</MenuItem>)}
                                    </Field>
                                </>
                                : <>
                                    <Typography paragraph display='inline'><b>Member's Email Address:</b>&nbsp;</Typography>
                                    <Field component={TextField} name='email' type='text' />
                                </>}
                        </div>
                        <div>
                            <Typography paragraph display='inline'><b>Role:</b>&nbsp;</Typography>
                            <ValuePickerControl value={selectedUserRole} items={userRoles} handleChange={handleRoleChange}></ValuePickerControl>
                        </div>
                    </Form>
                    <Button variant='contained' color='primary' size='medium' onClick={submitForm}>Save Changes</Button>
                </>
            )}
        </Formik>
    )
}